import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/format";

const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 India (+91)", maxDigits: 10, enabled: true },
  { code: "+1", label: "🇺🇸 USA / 🇨🇦 Canada (+1)", maxDigits: 10, enabled: false },
  { code: "+44", label: "🇬🇧 UK (+44)", maxDigits: 10, enabled: false },
  { code: "+971", label: "🇦🇪 UAE (+971)", maxDigits: 9, enabled: false },
  { code: "+65", label: "🇸🇬 Singapore (+65)", maxDigits: 8, enabled: false },
];

const splitE164 = (raw) => {
  const t = (raw || "").trim();
  if (!t) return { code: "+91", local: "" };
  if (t.startsWith("+91")) return { code: "+91", local: t.slice(3) };
  return { code: "+91", local: t.replace(/^\+/, "").replace(/^91/, "") };
};

export const OTPDialog = ({ phone: initialPhone = "", onVerified, trigger }) => {
  const seed = splitE164(initialPhone);
  const [open, setOpen] = useState(false);
  const [cc] = useState("+91"); // locked to +91 for the Twilio India trial
  const [local, setLocal] = useState(seed.local);
  const [code, setCode] = useState("");
  const [step, setStep] = useState("send");
  const [busy, setBusy] = useState(false);
  const [mock, setMock] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const fullPhone = `${cc}${local}`;
  const validLocal = /^[6-9]\d{9}$/.test(local); // Indian mobile pattern

  // Resend countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const send = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/otp/send", { phone: fullPhone });
      setMock(data.mock);
      setStep("verify");
      setResendIn(data.cooldown_sec || 60);
      toast.success(data.mock ? "Mock mode: use 123456 as the code" : `OTP sent to ${fullPhone}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const reason = (detail && typeof detail === "object" && detail.reason) || null;
      const retry = err.response?.headers?.["retry-after"]
        ? parseInt(err.response.headers["retry-after"], 10)
        : (detail && detail.retry_after) || 0;
      if (reason === "cooldown" && retry > 0 && retry <= 90) {
        setResendIn(retry);
        setStep("verify");
        toast.info(`A code was already sent to ${fullPhone}. Enter it below, or resend in ${retry}s.`);
      } else if (retry > 0) {
        setResendIn(retry);
        const mins = Math.ceil(retry / 60);
        toast.error(`Too many OTP requests for this number. Please try again in ~${mins} min.`);
      } else {
        toast.error(formatApiError(detail) || "Failed to send");
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/otp/verify", { phone: fullPhone, code });
      toast.success("Phone verified!");
      onVerified?.(fullPhone);
      setOpen(false);
      setStep("send");
      setCode("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setStep("send"); setCode(""); } }}>
      <DialogTrigger asChild>
        {trigger || (
          <button className="btn-ghost" data-testid="otp-open-btn">
            <Phone className="w-4 h-4" /> Verify Phone
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm" data-testid="otp-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[var(--emerald)]" /> Phone Verification
          </DialogTitle>
        </DialogHeader>
        {step === "send" ? (
          <form onSubmit={send} className="space-y-3">
            <div>
              <Label>Mobile number</Label>
              <div className="flex gap-2 mt-1">
                <select
                  value={cc}
                  disabled
                  title="More countries coming soon"
                  className="w-28 px-2 py-2 rounded-md border border-[var(--line)] bg-[var(--surface)] text-sm font-mono cursor-not-allowed"
                  data-testid="otp-country-code"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code} disabled={!c.enabled}>
                      {c.label}{!c.enabled && " · soon"}
                    </option>
                  ))}
                </select>
                <Input
                  required
                  type="tel"
                  inputMode="numeric"
                  placeholder="98xxxxxxxx"
                  value={local}
                  onChange={(e) => setLocal(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="flex-1 font-mono tracking-wide"
                  data-testid="otp-phone-input"
                  autoComplete="tel-national"
                />
              </div>
              <p className="text-xs text-[var(--ink-muted)] mt-1">Indian mobile only right now. Buyers reach you on this number via WhatsApp.</p>
            </div>
            <button
              type="submit"
              disabled={busy || !validLocal}
              className="btn-primary w-full"
              data-testid="otp-send-btn"
            >
              {busy ? "Sending…" : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-3">
            <div>
              <Label>Enter 6-digit code sent to {fullPhone}</Label>
              <Input
                required
                maxLength={6}
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="font-mono text-lg tracking-widest text-center"
                data-testid="otp-code-input"
                autoComplete="one-time-code"
                inputMode="numeric"
                autoFocus
              />
              {mock && (
                <p className="text-xs text-[var(--primary)] mt-1" data-testid="otp-mock-notice">
                  Mock mode active — use <strong>123456</strong>. (Twilio keys not set yet.)
                </p>
              )}
            </div>
            <button type="submit" disabled={busy || code.length !== 6} className="btn-primary w-full" data-testid="otp-verify-btn">
              {busy ? "Verifying…" : "Verify"}
            </button>
            <button
              type="button"
              onClick={() => send()}
              disabled={busy || resendIn > 0}
              className="btn-ghost w-full"
              data-testid="otp-resend-btn"
            >
              {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
            </button>
            <button type="button" onClick={() => setStep("send")} className="w-full text-xs text-[var(--ink-muted)] hover:text-[var(--primary)]">
              Change number
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
